import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { journey, setJourneyProgress, setActiveIndex } from "./journeyState";
import { SECTIONS, sectionIndexAt, sectionStart } from "./sections";
import { prefersReducedMotion } from "./quality";
import { measurePanels, clearPanels } from "./panelRegistry";

gsap.registerPlugin(ScrollTrigger);

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

/* px/ms of track motion that reads as "full speed" (velocity = 1) */
const FULL_SPEED = 2.2;

/* Navigate to a section from anywhere (navbar, hero CTA, footer). */
export function scrollToSection(id) {
  if (id === "top" && !journey.horizontal) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  if (journey.horizontal) {
    const top = sectionStart(id) * journey.maxScroll;
    window.scrollTo({ top, behavior: "smooth" });
  } else {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

/* ── Journey ──────────────────────────────────────────────
   Horizontal mode: the viewport is fixed; vertical scrolling
   translates a horizontal track (GPU transform only) while a
   spacer div provides the native scroll distance. The tween's
   own onUpdate publishes the ACTUAL track X + a velocity
   derived from it, so every subscriber (backdrop, panel
   reveal, parallax) moves in lockstep with what's on screen.
   Vertical mode (small screens): natural document flow, the
   same progress still drives the backdrop. */
export default function Journey({ horizontal, children }) {
  const trackRef = useRef(null);
  const spacerRef = useRef(null);

  useLayoutEffect(() => {
    journey.horizontal = horizontal;
    const reduced = prefersReducedMotion();

    if (!horizontal) {
      document.documentElement.classList.remove("no-scrollbar");
      clearPanels();
      journey.trackX = 0;
      const ids = SECTIONS.map((s) => s.id);
      const st = ScrollTrigger.create({
        trigger: document.body,
        start: "top top",
        end: "bottom bottom",
        onUpdate: (self) => {
          setJourneyProgress(self.progress, clamp(self.getVelocity() / 4000, -1, 1));
          // active section = last one whose top passed the viewport middle
          const mid = window.innerHeight * 0.5;
          let active = 0;
          ids.forEach((id, i) => {
            const el = document.getElementById(id);
            if (el && el.getBoundingClientRect().top <= mid) active = i;
          });
          setActiveIndex(active);
        },
      });
      return () => st.kill();
    }

    document.documentElement.classList.add("no-scrollbar");
    const track = trackRef.current;
    const spacer = spacerRef.current;

    const distance = () => Math.max(track.scrollWidth - window.innerWidth, 1);

    const layout = () => {
      const dist = distance();
      journey.maxScroll = dist;
      spacer.style.height = `${dist + window.innerHeight}px`;
      measurePanels(track);
    };
    layout();

    let prevX = 0;
    let prevT = performance.now();

    const tween = gsap.to(track, {
      x: () => -distance(),
      ease: "none",
      scrollTrigger: {
        trigger: spacer,
        start: "top top",
        end: "bottom bottom",
        scrub: reduced ? true : 0.6,
        invalidateOnRefresh: true,
      },
      onUpdate() {
        const x = Number(gsap.getProperty(track, "x"));
        const now = performance.now();
        const dt = Math.max(now - prevT, 1);
        // track moves left (x decreases) as the journey advances
        const vel = clamp(-(x - prevX) / dt / FULL_SPEED, -1, 1);
        prevX = x;
        prevT = now;

        const p = this.progress();
        journey.trackX = x;
        setJourneyProgress(p, vel);
        setActiveIndex(sectionIndexAt(p));
      },
    });

    // publish the initial state once so subscribers paint frame 0
    journey.trackX = 0;
    setJourneyProgress(0, 0);
    setActiveIndex(0);

    const onResize = () => {
      layout();
      ScrollTrigger.refresh();
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      tween.scrollTrigger?.kill();
      tween.kill();
      document.documentElement.classList.remove("no-scrollbar");
    };
  }, [horizontal]);

  if (!horizontal) {
    return <main className="relative z-10">{children}</main>;
  }

  return (
    <>
      <div className="fixed inset-0 z-10 overflow-hidden">
        <main ref={trackRef} className="flex h-full w-max will-change-transform">
          {children}
        </main>
      </div>
      <div ref={spacerRef} aria-hidden />
    </>
  );
}
